import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ErreurApi, EVENEMENT_SESSION_EXPIREE, appelApi, urlApi } from './api';
import { parametresVueGlobale } from './useVueGlobale';
import type { CompteMontant, ControleFacture, LigneFactureCircuit } from './useCircuitFactures';
import type { StatutFacture, TypeFacture } from './useFormateurs';

// ---------------------------------------------------------------------------
// Réception des factures formateurs (TF-402), écran « Factures formateurs » :
// le registre du pôle facturation. Les factures reçues dans la période, leurs
// contrôles SIRET, RIB et montant, les lignes « Bulletin de paie » des
// salariés et les refus des gestes viennent de l'API : l'écran ne décide rien.
// ---------------------------------------------------------------------------

export type ChoixPeriodeReception = 'MOIS' | 'MOIS_PRECEDENT' | 'TRIMESTRE' | 'ANNEE' | 'LIBRE';
export type EtatReception = StatutFacture | 'BULLETIN_DE_PAIE';

export interface LigneReception extends LigneFactureCircuit {
  controles: ControleFacture[];
  alertes: number;
  statutProfessionnel: string;
  recueLe: string;
  siret: string;
  ibanMasque: string | null;
  titulaireCompte: string | null;
}

export interface LigneBulletinDePaie {
  cle: string;
  formateur: { id: string; nom: string };
  statutProfessionnel: string;
  siteCode: string;
  type: TypeFacture;
  typeLibelle: string;
  heures: number;
  fiches: number;
  etatLibelle: string;
  etatTon: string;
}

export interface VueReception {
  periode: { choix: ChoixPeriodeReception; du: string; au: string; libelle: string };
  compteurs: {
    aVerifier: CompteMontant;
    verifiees: CompteMontant;
    transmisesDirection: CompteMontant;
    periode: { factures: number; montant: number; formateurs: number };
  };
  aTransmettre: number;
  factures: LigneReception[];
  salaries: LigneBulletinDePaie[];
  rythmes: { type: TypeFacture; libelle: string; detail: string }[];
  options: {
    periodes: { choix: ChoixPeriodeReception; libelle: string }[];
    etats: { valeur: EtatReception; libelle: string }[];
  };
  peutValider: boolean;
  peutPayer: boolean;
}

export interface FiltresReception {
  site: string;
  periode: ChoixPeriodeReception;
  du: string;
  au: string;
  etat: EtatReception | '';
  type: TypeFacture | '';
  recherche: string;
}

/** Une période libre incomplète ou à l'envers n'est pas demandée. */
export function periodeIncomplete(filtres: Pick<FiltresReception, 'periode' | 'du' | 'au'>): boolean {
  return filtres.periode === 'LIBRE' && (!filtres.du || !filtres.au || filtres.du > filtres.au);
}

function chaine(filtres: FiltresReception): string {
  const libre = filtres.periode === 'LIBRE';
  return parametresVueGlobale({ ...filtres, du: libre ? filtres.du : '', au: libre ? filtres.au : '' });
}

export function useReceptionFactures(filtres: FiltresReception) {
  const parametres = chaine(filtres);
  return useQuery({
    // Sous la clé du circuit : un geste relit aussi le registre.
    queryKey: ['factures-formateurs', 'reception', parametres],
    queryFn: () => appelApi<VueReception>(`/factures-formateurs/reception?${parametres}`),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
    enabled: !periodeIncomplete(filtres),
  });
}

/**
 * Exporte le registre de la période en Excel, aux filtres de l'écran. Passe
 * par `fetch` : un refus de l'API s'affiche à l'écran au lieu de s'enregistrer
 * comme un faux fichier.
 */
export async function exporterReception(filtres: FiltresReception): Promise<{ nomFichier: string }> {
  const reponse = await fetch(urlApi(`/factures-formateurs/reception/export?${chaine(filtres)}`), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => null)) as
      | { erreur?: { code?: string; message?: string; details?: unknown } }
      | null;
    if (reponse.status === 401) window.dispatchEvent(new Event(EVENEMENT_SESSION_EXPIREE));
    throw new ErreurApi(
      reponse.status,
      erreur?.erreur?.code ?? 'ERREUR',
      erreur?.erreur?.message ?? 'L’export n’a pas pu être produit.',
      erreur?.erreur?.details,
    );
  }
  const nomFichier =
    /filename="([^"]+)"/.exec(reponse.headers.get('content-disposition') ?? '')?.[1] ?? 'factures-formateurs.xlsx';
  const url = URL.createObjectURL(await reponse.blob());
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nomFichier;
  document.body.appendChild(lien);
  lien.click();
  document.body.removeChild(lien);
  URL.revokeObjectURL(url);
  return { nomFichier };
}
